import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Clock, XCircle } from "lucide-react";
import api, { apiError, LOGO } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PayPanel, CYCLE } from "./PayPanel";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }) : "—");

function Data({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-white/45">{label}</p>
      <p className="font-semibold text-white mt-0.5" data-testid={testid}>{value || "—"}</p>
    </div>
  );
}

export default function LicenseLock() {
  const { logout, reload } = useAuth();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/billing/status").then((r) => setSt(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); const t = setInterval(() => { reload(); load(); }, 30000); return () => clearInterval(t); }, [reload]);
  const out = () => { logout(); navigate("/login"); };
  const lic = st?.license || {};
  const review = (st?.orders || []).find((o) => o.status === "en_revision");
  const rejected = (st?.orders || [])[0]?.status === "rechazado" ? st.orders[0] : null;
  return (
    <div className="min-h-screen bg-[#0B1220] text-white flex items-start lg:items-center justify-center p-4 py-10" data-testid="license-lock-screen">
      <div className="w-full max-w-5xl grid lg:grid-cols-[1fr_1.15fr] gap-6">
        <div className="rounded-3xl bg-[#111827] border border-white/10 p-7 relative overflow-hidden">
          <span className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#DC2626]/20" />
          <img src={LOGO} alt="ProfesionalPOS" className="w-36 rounded-xl bg-white p-2 relative" />
          <div className="mt-8 flex items-center gap-3 relative">
            <span className="w-12 h-12 rounded-2xl bg-[#DC2626] flex items-center justify-center"><Lock size={22} /></span>
            <div>
              <h1 className="font-heading text-3xl font-extrabold leading-tight">Licencia vencida</h1>
              <p className="text-white/60 text-sm">El acceso al sistema está bloqueado hasta que se pague la licencia.</p>
            </div>
          </div>
          {err && <p className="mt-6 text-sm text-[#FCA5A5]">{err}</p>}
          {st && (
            <div className="grid grid-cols-2 gap-3 mt-8 relative" data-testid="license-lock-data">
              <Data label="Negocio" value={st.restaurant_name} testid="lock-restaurant" />
              <Data label="Código" value={st.restaurant_code} testid="lock-code" />
              <Data label="Plan" value={lic.plan_name || st.plan?.name} testid="lock-plan" />
              <Data label="Ciclo" value={CYCLE[lic.billing_cycle] || lic.billing_cycle} testid="lock-cycle" />
              <Data label="Venció el" value={fmtDate(lic.end_date)} testid="lock-end-date" />
              <Data label="Terminales" value={lic.max_stations} testid="lock-stations" />
            </div>
          )}
          <button data-testid="lock-logout" onClick={out} className="mt-8 flex items-center gap-2 text-sm text-white/60 hover:text-white relative"><LogOut size={16} /> Cerrar sesión</button>
        </div>
        <div className="rounded-3xl bg-white text-[#111827] p-7">
          {!st ? <p className="text-[#111827]/50">Cargando…</p> : !st.can_pay ? (
            <div data-testid="lock-no-pay" className="h-full flex flex-col justify-center">
              <h2 className="font-heading text-2xl font-extrabold">Avisa a tu administrador</h2>
              <p className="text-[#111827]/60 mt-2">{st.is_branch ? "La licencia se paga desde el negocio principal." : "Solo el Administrador del negocio puede renovar la licencia."} Cuando se pague, el sistema se desbloquea solo.</p>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-extrabold">Renueva tu licencia</h2>
              <p className="text-[#111827]/60 text-sm mt-1 mb-5">Elige tu plan y si lo quieres mensual o anual. Al confirmarse el pago se desbloquean todas las funciones del plan.</p>
              {review && (
                <div className="mb-4 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-3 text-sm flex gap-2" data-testid="lock-review-banner">
                  <Clock size={18} className="text-[#B45309] shrink-0" />
                  <span>Tu comprobante de {review.plan_name} por <b>${Number(review.amount).toFixed(2)}</b> está en revisión. Se desbloqueará al aprobarse.</span>
                </div>
              )}
              {rejected && (
                <div className="mb-4 rounded-xl bg-[#DC2626]/10 border border-[#DC2626]/25 p-3 text-sm flex gap-2" data-testid="lock-rejected-banner">
                  <XCircle size={18} className="text-[#DC2626] shrink-0" /><span>Tu último comprobante fue rechazado: {rejected.reject_reason}</span>
                </div>
              )}
              {!review && <PayPanel status={st} kind="renew" onDone={load} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
